import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { icons } from "../../../icons";
import { StartPage, Button, ModalVariables, Label, FilterBar, SearchComponent } from "../../../components";
import { useNavigate } from "react-router";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { volumeSnapshotsService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import { useAuth } from "../../../context/user.context";
import { compareArrays } from "../../../utils/findIntersection";
import useUpdateEffect from "../../../hooks/useUpdateEffect";

const SNAPSHOT_FILTER_KEYS = ['created_at', 'status', 'volume'];
const DEFAULT_ORDERING = '-created_at';

const VolumeSnapshots: FC = () => {
  const {
    viewAlert,
  } = useUi()
  const { user, quotas } = useAuth()
  const [snapshots, setSnapshots] = useState<any[]>([])
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering = '-created_at', setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [paginationData, setPaginationData] = useState<any>()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [selectedSnapshot, setSelectedSnapshot] = useState<any>()
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const [filterOptions, setFilterOptions] = useState<any>()
  const normalizedFilterOptions = useMemo(() => {
    if (!filterOptions) return undefined;
    return SNAPSHOT_FILTER_KEYS.reduce((acc:any, key) => {
      if (filterOptions?.[key]?.length) {
        acc[key] = filterOptions[key];
      }
      return acc;
    }, {});
  }, [filterOptions]);
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
    setChecked([]);
  }

  const editChecked = (id: any, isAll?: boolean) => {
    if (id === null) {
      setChecked([])
      return
    }
    const founded = checked.find((item: any) => item.id === id?.id)
    if (founded) {
      if (isAll) {
        return
      } else {
        setChecked((prev: any) => {
          return prev.filter((item: any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  }

  useEffect(() => {
    if (compareArrays(snapshots, checked) && checked?.length !== 0) {
      setAllChecked(true)
    }
  }, [checked])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);

  const [t] = useTranslation()
  const navigate = useNavigate()

  const _updateSnapshots = () => {
    volumeSnapshotsService.getSnapshotList({
      page: page || 1,
      page_size: pageSize || 10,
      ordering: ordering || DEFAULT_ORDERING,
      search: searchString || '',
      filtering: filtering || '',
    })
      .then((res) => {
        setFilterOptions(res?.data?.filter_options);
        setSnapshots(res.data.objects.map((snapshot: any) => {
          return {
            ...snapshot,
            navigate: snapshot.id,
            sizeString: `${snapshot.size} GB`,
            nameStr: snapshot.name || snapshot.id,
            name: (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: '1px'
              }}>
                {snapshot.name || snapshot.id}
                {snapshot.description &&
                  <Label
                    title=''
                    tooltipText={snapshot.description}
                  />
                }
              </div>),
            created_at: timeConverterMMDDYYHHMM(snapshot.created_at),
          }
        }))
        setPaginationData(res.data)
        setPageLoading(false)
      })
      .catch((e) => {
        viewAlert({
          severity: 'error',
          message: e?.response?.data?.detail || `${t("ERROR_OCCURRED")}`,
        })
        setPageLoading(false)
      })
  }

  useEffect(() => {
    _updateSnapshots()
    const interval = setInterval(() => {
      _updateSnapshots()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  }, [page,
    ordering,
    pageSize,
    searchString,
    filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])

  const openModal = (modal: any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }

  const closeModal = async (values: any, setErrors?: any) => {
    let res: any;
    try {
      setLoading(true);
      switch (modalType) {
        case 'create-volume-snapshot':
          res = await volumeSnapshotsService.createSnapshot(values);
          if (res?.data?.detail) {
            viewAlert({
              severity: 'info',
              message: res.data?.detail,
            });
          }
          _updateSnapshots();
          break;
        case "delete":
          try {
            if (allChecked) {
              await Promise.all(snapshots.map((item) => volumeSnapshotsService.deleteSnapshot(item.id)));
              _updateSnapshots();
              setAllChecked(false);
              setChecked([]);
            } else if (checked?.length > 0) {
              await Promise.all(checked.map((item) => volumeSnapshotsService.deleteSnapshot(item.id)));
              _updateSnapshots();
              setChecked([]);
            } else if (selectedSnapshot) {
              await volumeSnapshotsService.deleteSnapshot(selectedSnapshot.id);
              _updateSnapshots();
            }
          } catch (e: any) {
            if (e?.response?.data.detail) {
              viewAlert({
                severity: 'error',
                message: e.response.data.detail,
              });
            }
            setLoading(false);
          } finally {
            setViewModal(false);
            setActiveAnimation(false);
          }
          break;
      }
      if (modalType !== 'delete') {
        setViewModal(false)
        setActiveAnimation(false)
      }
    } catch (e: any) {
      if (e?.response?.data?.detail || e?.response?.data?.non_field_errors) {
        viewAlert({
          severity: 'error',
          message: e.response?.data?.detail || e?.response?.data?.non_field_errors,
        });
      } else if (setErrors) {
        setErrors(e.response.data)
      }
    } finally {
      if (modalType !== 'delete') {
        setLoading(false);
      }
    }
  }

  const canDelete = paginationData?.permissions?.['volumesnapshots.destroy'] !== false

  const items = canDelete ? [
    {
      title: t("DELETE"),
      id: '4',
      onClick: (snapshot: any) => {
        openModal('delete')
        setAlertTitle(t("SNAPSHOT"))
        setAlertSubTitle([snapshot])
        setSelectedSnapshot(snapshot)
      },
      icon: icons.DeleteIcon,
      divider: false
    },
  ] : []

  const _createFunc = () => {
    openModal('create-volume-snapshot')
  }

  const canCreate = paginationData?.permissions?.['volumesnapshots.create'] !== false

  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }

  const Columns = [
    {
      title: t("NAME"),
      key: 'name',
      sorted:true,
    },
    {
      title: t("CREATED_AT"),
      key: 'created_at',
      sorted:true,
    },
    {
      title: t("SIZE"),
      key: 'sizeString'
    },
    {
      title: t("STATUS"),
      key: 'status'
    },
    {
      title: '',
      key: 'props'
    },
  ]

  return (
    <>
      <StartPage
        title={`${t("VOLUME_SNAPSHOTS")}`}
        noResourceText={`${t("NO_VOLUME_SNAPSHOTS")}`}
        createResourceButton={canCreate ? `${t('CREATE_VOLUME_SNAPSHOT')}` : undefined}
        createResourceButtonEventOrLink={canCreate ? _createFunc : undefined}
        haveResource={snapshots && snapshots.length > 0}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              {canCreate && (
                <Button
                  title={t('CREATE_VOLUME_SNAPSHOT')}
                  onClick={_createFunc}
                  variant='primary'
                  size='custom'
                  customStyles={{height:'40px', whiteSpace:'nowrap'}}
                />
              )}
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginLeft:'auto'}}>
                {canDelete && (
                  <Button
                    onClick={() => {
                      setAlertTitle(t("VOLUME_SNAPSHOTS"))
                      setAlertSubTitle(allChecked ? snapshots : checked)
                      openModal('delete')
                    }}
                    variant='icon'
                    size='display'
                    test_id='button-delete'
                    disabled={!allChecked && checked?.length === 0}
                  >
                    <img src={icons.DeleteIcon} />
                  </Button>
                )}
                <Button
                  title={t("CLEAR_FILTERS")}
                  onClick={handleResetFilters}
                  variant='stroke'
                  size='display'
                  disabled={
                    (!filtering || filtering === '') &&
                    !searchString &&
                    ordering === DEFAULT_ORDERING
                  }
                />
              </div>
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        pageLoading={pageLoading}
        tableData={{
          columns: Columns,
          data: snapshots,
          props: items,
          allChecked: allChecked,
          setAllChecked: setAllChecked,
          checked: checked,
          setChecked: editChecked,
          paginationData: paginationData,
          updateData: _updateSnapshots
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
      />
    </>
  )
}

export default VolumeSnapshots;
